'use client';

import * as React from 'react';
import { Participant, ParticipantEvent, RoomEvent, Track } from 'livekit-client';
import type { LocalTrackPublication } from 'livekit-client';
import { useConnectionQualityIndicator, useRoomContext } from '@livekit/components-react';
import {
  resolveConnectionQualityDisplay,
  type ConnectionQualityDisplayState,
  type DisplayConnectionQuality,
} from './connectionQualityDisplay';

const TICK_MS = 1_000;

export function useDebouncedConnectionQuality(participant: Participant) {
  const { quality: rawQuality } = useConnectionQualityIndicator({ participant });
  const room = useRoomContext();

  const stateRef = React.useRef<ConnectionQualityDisplayState>({
    poorSince: null,
    lastStableDisplay: 'good',
    screenShareStartedAt: null,
  });

  const [displayQuality, setDisplayQuality] = React.useState<DisplayConnectionQuality>('good');
  const [isDebouncingPoor, setIsDebouncingPoor] = React.useState(false);
  const [showTransientHint, setShowTransientHint] = React.useState(false);

  const recompute = React.useCallback(() => {
    const result = resolveConnectionQualityDisplay({
      rawQuality: rawQuality,
      now: Date.now(),
      participantIsLocal: participant.isLocal,
      state: stateRef.current,
    });

    stateRef.current = {
      poorSince: result.poorSince,
      lastStableDisplay: result.lastStableDisplay,
      screenShareStartedAt: result.screenShareStartedAt,
    };
    // Só re-renderiza se algo mudou (antes: setState a cada 400ms por tile).
    setDisplayQuality((prev) => (prev === result.displayQuality ? prev : result.displayQuality));
    setIsDebouncingPoor((prev) => (prev === result.isDebouncingPoor ? prev : result.isDebouncingPoor));
    setShowTransientHint((prev) => (prev === result.showTransientHint ? prev : result.showTransientHint));
  }, [participant.isLocal, rawQuality]);

  React.useEffect(() => {
    recompute();
    const interval = setInterval(recompute, TICK_MS);
    return () => clearInterval(interval);
  }, [recompute]);

  /** Grace de screenshare só afeta o indicador do participante local. */
  React.useEffect(() => {
    if (!participant.isLocal || !room) {
      return;
    }

    const lp = room.localParticipant;

    const onPublished = (publication: LocalTrackPublication) => {
      if (publication.source !== Track.Source.ScreenShare) {
        return;
      }
      stateRef.current.screenShareStartedAt = Date.now();
      recompute();
    };

    const onUnpublished = (publication: LocalTrackPublication) => {
      if (publication.source !== Track.Source.ScreenShare) {
        return;
      }
      const stillSharing = lp.getTrackPublication(Track.Source.ScreenShare)?.track;
      if (stillSharing) {
        return;
      }
      stateRef.current.screenShareStartedAt = null;
      recompute();
    };

    const onReconnected = () => {
      stateRef.current.poorSince = null;
      stateRef.current.screenShareStartedAt = null;
      recompute();
    };

    lp.on(ParticipantEvent.LocalTrackPublished, onPublished);
    lp.on(ParticipantEvent.LocalTrackUnpublished, onUnpublished);
    room.on(RoomEvent.Reconnected, onReconnected);

    return () => {
      lp.off(ParticipantEvent.LocalTrackPublished, onPublished);
      lp.off(ParticipantEvent.LocalTrackUnpublished, onUnpublished);
      room.off(RoomEvent.Reconnected, onReconnected);
    };
  }, [participant.isLocal, recompute, room]);

  return {
    quality: displayQuality,
    rawQuality,
    isDebouncingPoor,
    showTransientHint,
  };
}
